<template>
  <div class="main-view-wrapper">
    <!-- 基础表单区域 -->
    <el-row :gutter="20">
      <el-col v-for="(module, module_key) in basic_form_preview" :key="module_key" v-bind="module.props.col">
        <el-card :ref="`basic_deploy_${module_key}`" shadow="never" class="mb-20">
          <div slot="header" class="w-full flex justify-between items-center">
            <h2 class="m-0">{{ module.title }}</h2>
            <div v-if="is_set_dynamic_component_fail && module_key === 'creative_config'" class="cursor-pointer setting-btn" @click="setDynamicComponent">重新生成创意</div>
            <div v-else class="cursor-pointer setting-btn" @click="() => onBasicDeploy(module_key)">配置</div>
          </div>
          <el-empty v-if="isAddCreative && module_key === 'campaign_config'" style="height: 400px;" description="暂无配置" />
          <step-form v-else :parent-key="module_key" :form-fields="module" />
        </el-card>
      </el-col>
    </el-row>
    <!-- 分配表单区域 -->
    <el-row :gutter="20">
      <el-col v-for="(module, module_key) in assign_form_fields" :key="module_key" v-bind="module.props.col">
        <el-card :body-style="{ padding: 0 }" shadow="never" class="mb-20">
          <div slot="header" class="w-full flex justify-between items-center">
            <h2 class="m-0">{{ module.title }}</h2>
            <div class="flex justify-between items-center">
              <div v-if="module_key === 'material_config'" class="cursor-pointer setting-btn mr-10" @click="() => shuffleModule(module_key)">重新排序</div>
              <div v-if="support_title_component && module_key === 'title_config'" class="cursor-pointer setting-btn mr-10" @click="() => onUseMediaComponent(module_key)">引用组件</div>
              <div v-if="isSupportAssignConfig(module_key)" class="cursor-pointer setting-btn" @click="() => onAssignDeploy(module_key)">配置</div>
            </div>
          </div>
          <component :is="assign_form_config.preview_components_map[module_key]" :form-fields="module" v-bind="assign_form_config.getPreviewComponentsProps[module_key]?.() || {}" />
        </el-card>
      </el-col>
    </el-row>
    <div class="w-full text-center">
      <el-button :class="{ invisible: !is_show_preview_btn }" type="primary" icon="el-icon-view" @click="onPreview">提交预览</el-button>
    </div>
    <!-- 基础表单部署 -->
    <el-dialog v-if="step_form_config.is_show" v-bind="step_form_config.props" :visible.sync="step_form_config.is_show">
      <el-row :gutter="20">
        <el-col v-show="step_form_config.steps.length" :span="4">
          <anchor-pos :steps="step_form_config.steps" @anchor="onAnchor" />
        </el-col>
        <el-col :span="step_form_config.steps.length ? 20 : 24">
          <step-form
            ref="step_form_ref"
            :parent-key="step_form_config.field_key"
            :form-fields="step_form_config.form_fields"
            :steps="step_form_config.steps"
          />
        </el-col>
      </el-row>
      <div slot="footer">
        <div class="w-full flex items-center justify-center">
          <el-button type="primary" icon="el-icon-position" @click="onBasicSubmitDeploy">提交配置</el-button>
          <el-button icon="el-icon-close" @click="onBasicCannelDeploy">取消</el-button>
        </div>
      </div>
    </el-dialog>
    <!-- 分配表单部署 -->
    <div v-if="assign_form_config.is_show">
      <!-- 自带dialog的组件 -->
      <components
        :is="assign_form_config.components_map[assign_form_config.field_key]()"
        v-if="['material_config'].includes(assign_form_config.field_key)"
        ref="assign_edit_form_ref"
        :form-fields="assign_form_config.form_fields"
        :is-show="assign_form_config.is_show"
        @confirm="onAssignSubmitDeploy"
        @cannel="onAssignCannelDeploy"
      />
      <!-- 不带dialog的组件 -->
      <el-dialog v-else v-bind="assign_form_config.props" :visible.sync="assign_form_config.is_show">
        <components :is="assign_form_config.components_map[assign_form_config.field_key]()" ref="assign_edit_form_ref" :form-fields="assign_form_config.form_fields" />
        <div slot="footer">
          <div class="w-full flex items-center justify-center">
            <el-button type="primary" icon="el-icon-position" @click="onAssignSubmitDeploy">提交配置</el-button>
            <el-button icon="el-icon-close" @click="onAssignCannelDeploy">取消</el-button>
          </div>
        </div>
      </el-dialog>
    </div>
    <div v-if="is_show_preview_table" class="w-full">
      <!-- 预览表格 -->
      <div class="mt-20 mb-20 w-full">
        <preview-table ref="preview_table_ref" :data-sources="dynamic_table_sources" :form-assign-data="raw_form_assign_data" :options-enums-val="options_enums_val" :options-val="options_val" :is-add-creative="isAddCreative" :title-component-type="assign_title_component_type" @copy="onCopy" @remove="onRemove" />
      </div>
      <div class="w-full text-center">
        <el-button :loading="submit_loading" type="primary" icon="el-icon-position" @click="onSubmit">创建广告</el-button>
        <span class="ml-20"><el-switch v-model="is_save_template" :disabled="isAddCreative" /><span class="ml-10" style="color: #409EFF">自动保存为模板</span></span>
      </div>
    </div>
  </div>
</template>

<script>
import { data_sharing_mixin } from '@/views/fusion/investment/intelligence-create/mixins/data-sharing';
import { transformFormToPreview } from '@/tools/form';
import { clone, convertPathObject, randomUUID, unique, shuffleArray } from '@/utils';
import { rulesValidator } from '@/utils/validator';
import { executeFormFieldsEventListener, applyFormFieldsOverrides, modulesCarousel } from '@/views/fusion/investment/intelligence-create/tools';
import {
  CAMPAIGN_MODULE_KEY,
  CREATIVE_MODULE_KEY,
  TARGETING_MODULE_KEY,
  MATERIAL_MODULE_KEY,
  TITLE_MODULE_KEY,
  SITE_MODULE_KEY,
} from '@/views/fusion/investment/intelligence-create/strategy/constants';
import { generateAdvertiserSourcesData, generateDynamicTableData } from '@/views/fusion/investment/intelligence-create/tools/table';
import { pullChannelPackageApi } from '@/api/tool/channel-package';
import { getPreviewPlanApi, createPlanApi } from '@/api/plan';
import { CHANNEL_INTERFACE_TYPE, OPTIONS_TYPE } from '@/enums';

import TemplateResource from '@/api/template';
const templateResource = new TemplateResource();

export default {
  components: {
    'step-form': () => import('./components/step-form/index.vue'),
    'anchor-pos': () => import('./components/anchor-pos/index.vue'),
    'title-edit-form': () => import('./components/assign-form/title/edit-form/index.vue'),
    'targeting-edit-form': () => import('./components/assign-form/targeting/edit-form/index.vue'),
    'material-edit-form': () => import('./components/assign-form/material/edit-form/index.vue'),
    'site-edit-form': () => import('./components/assign-form/site/edit-form/index.vue'),
    'material-view-form': () => import('./components/assign-form/material/view-form/index.vue'),
    'title-view-form': () => import('./components/assign-form/title/view-form/index.vue'),
    'public-assign-view-form': () => import('./components/assign-form/components/view-form/index.vue'),
    'preview-table': () => import('./components/preview-table/index.vue'),
    'title-component': () => import('./components/assign-form/title/component/index.vue'),
  },
  mixins: [data_sharing_mixin],
  props: {
    /** 是否为模板数据 */
    isTemplate: {
      type: Boolean,
      required: true,
    },
    /** 是否为添加创意 */
    isAddCreative: {
      type: Boolean,
      required: true,
    },
    /** 添加创意的数据 */
    addCreativeList: {
      type: Array,
      default: () => [],
    },
    formFields: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      // 基础表单配置
      step_form_config: {
        is_show: false, // 是否显示弹窗
        field_key: '', // 模块的键名
        form_fields: {}, // 模块数据
        steps: [], // 步骤条数据
        // 弹窗的属性配置
        props: {
          title: '',
          width: '1200px',
        },
      },
      // 分配表单配置
      assign_form_config: {
        is_show: false, // 是否显示弹窗
        field_key: '', // 模块的键名
        form_fields: {}, // 模块数据
        // 弹窗的属性配置
        props: {
          top: '10vh',
          title: '',
          width: '1200px',
        },
        // 分配表单编辑组件名称映射
        components_map: {
          targeting_config: () => 'targeting-edit-form',
          material_config: () => 'material-edit-form',
          title_config: () => {
            const is_component = this.assign_form_config.form_fields.type === 'component';
            return is_component ? 'title-component' : 'title-edit-form';
          },
          site_config: () => 'site-edit-form',
        },
        // 分配表单预览组件名称映射
        preview_components_map: {
          targeting_config: 'public-assign-view-form',
          material_config: 'material-view-form',
          title_config: 'title-view-form',
          site_config: 'public-assign-view-form',
        },
        // 分配表单预览组件props
        getPreviewComponentsProps: {
          material_config: () => {
            const { config: { init_ops: { channel_interface }}} = this.local_sharing_data.getFormContext();
            const strategy = {
              [CHANNEL_INTERFACE_TYPE.BAIDU]: () => ({ showHorizontalCover: true }),
              default: () => {},
            };
            return (strategy[channel_interface] || strategy['default'])();
          },
          title_config: () => {
            return {
              showEditButton: this.formFields.title_config.type === 'library',
            };
          },
          site_config: () => {
            return {
              isShowFilter: true,
            };
          },
        },
      },
      // 所有表单提交数据：模块名：提交数据
      form_model: {},
      // 所有模块分配的数据：转化后的数据
      form_assign_data: {},
      // 所有模块分配的数据：原始数据, 用于传递给预览表格
      raw_form_assign_data: {},
      // 表格数据源
      dynamic_table_sources: [],
      // 账户源数据
      advertiser_sources: {},
      // 创建广告按钮loading
      submit_loading: false,
      // 提交预览时生成的选项枚举值
      options_enums_val: {},
      // 提交预览时生成的选项值
      options_val: {},
      // 是否保存为模板
      is_save_template: true,
      // 模板数据
      template_data: {},
      // 渲染动态创意参数
      set_dynamic_component_params: {},
      // 是否渲染动态创意失败
      is_set_dynamic_component_fail: false,
      /** 当前分配的标题组件的类型，默认标题库, 用于传递给预览表格 */
      assign_title_component_type: 'library',
    };
  },
  computed: {
    // 是否显示预览表格
    is_show_preview_table() {
      return !!this.local_sharing_data.getFormContext() && this.dynamic_table_sources.length;
    },
    /** 基础表单配置 */
    basic_form_fields() {
      const basic_fields_keys = [CAMPAIGN_MODULE_KEY, CREATIVE_MODULE_KEY];
      return Object.fromEntries(Object.entries(this.formFields).filter(([key]) => basic_fields_keys.includes(key)));
    },
    /** 分配表单配置 */
    assign_form_fields() {
      const assign_fields_keys = [TARGETING_MODULE_KEY, MATERIAL_MODULE_KEY, TITLE_MODULE_KEY, SITE_MODULE_KEY];
      return Object.fromEntries(Object.entries(this.formFields).filter(([key]) => assign_fields_keys.includes(key)));
    },
    /** 基础表单预览配置 */
    basic_form_preview() {
      return this.generateBasicFormPreview(this.basic_form_fields);
    },
    // 是否显示预览按钮
    is_show_preview_btn() {
      const { strategy } = this.local_sharing_data.getFormContext();
      return strategy.isShowPreviewBtn(this.form_assign_data, this.formFields, {
        is_add_creative: this.isAddCreative,
      });
    },
    /** 是否支持标题文案组件 */
    support_title_component() {
      const { config: { init_ops: { channel_interface }}} = this.local_sharing_data.getFormContext();
      const channels = [CHANNEL_INTERFACE_TYPE.GDT];
      return channels.includes(channel_interface);
    },
  },
  created() {
    this.is_save_template = !this.isAddCreative;
  },
  mounted() {
    (!this.isTemplate && !this.isAddCreative) && this.onBasicDeploy(CAMPAIGN_MODULE_KEY);
  },
  methods: {
    /**
     * 是否支持分配配置
     * @param {string} module_key 模块键名
     * @returns {boolean}
     */
    isSupportAssignConfig(module_key) {
      const { config: { init_ops: { channel_interface }}} = this.local_sharing_data.getFormContext();

      const strategy = {
        [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
          /**
           * 模块满足以下条件时，不支持分配配置
           * - 素材 - 关联星广联投为【启用(ON)】 或 素材类型为【直播素材['LIVE_MATERIALS']】
           * - 标题 - 使用原视频标题为【启用(ON)】 或 素材类型为【直播素材['LIVE_MATERIALS']】
           * - 落地页
           *   - 素材类型可见 且 投放平台为【Android应用['ANDROID']】或投放平台为【IOS应用['IOS']】
           *     - 素材类型为【直播素材['LIVE_MATERIALS']】
           *     - 素材类型为【广告素材['PROMOTION_MATERIALS']】 且 营销场景为【直播['LIVE']】
           */
          const channelJudgment = () => {
            const { campaign_config, creative_config } = this.formFields;
            const { delivery_form: { config: { delivery_platform_type, marketing_goal, star_task_switch }}} = campaign_config.config;
            const { content_form: { config: { materials_type, original_video_title }}} = creative_config.config;

            const is_app = ['ANDROID', 'IOS'].includes(delivery_platform_type.value);
            const is_live = marketing_goal.value === 'LIVE';
            const is_materials_type_visible = !materials_type.hidden;
            const is_promotion_materials = materials_type.value === 'PROMOTION_MATERIALS';
            const is_live_materials = materials_type.value === 'LIVE_MATERIALS';
            const is_star_tasks_on = star_task_switch.value === 'ON';
            const is_original_video_title_show = !original_video_title.hidden;
            const is_original_video_title_on = original_video_title.value === 'ON';

            const judgment = [
              (module_key === MATERIAL_MODULE_KEY && (is_star_tasks_on || is_live_materials)),
              (module_key === TITLE_MODULE_KEY && ((is_original_video_title_show && is_original_video_title_on) || (is_materials_type_visible && is_live_materials))),
              (module_key === SITE_MODULE_KEY && (is_materials_type_visible && is_app && (is_live_materials || (is_promotion_materials && is_live)))),
            ];

            return !(judgment.some(j => j));
          };
          return channelJudgment();
        },
        [CHANNEL_INTERFACE_TYPE.XIAOMI]: () => {
          /**
           * 模块满足以下条件时，不支持分配配置
           * - 落地页 - 不支持
           */
          const channelJudgment = () => !(module_key === SITE_MODULE_KEY);
          return channelJudgment();
        },
        [CHANNEL_INTERFACE_TYPE.GDT]: () => {
          /**
           * 模块满足以下条件时，不支持分配配置
           * - 落地页 - 落地页跳转类型为【一键下载[PAGE_TYPE_ANDROID_DIRECT_DOWNLOAD]】、【App Store下载页[PAGE_TYPE_IOS_APP]】和【微信小游戏[PAGE_TYPE_WECHAT_MINI_GAME]】之一
           */
          const channelJudgment = () => {
            const { creative_config } = this.formFields;
            const { content_form: { config: { site_type = {}}}} = creative_config.config;

            const is_match = ['PAGE_TYPE_ANDROID_DIRECT_DOWNLOAD', 'PAGE_TYPE_IOS_APP', 'PAGE_TYPE_WECHAT_MINI_GAME'].includes(site_type.value);

            return !(is_match && module_key === SITE_MODULE_KEY);
          };

          return channelJudgment();
        },
        default: () => true,
      };

      const defaultJudgment = () => {
        /**
           * 模块满足以下条件时，不支持分配配置
           * - 广告定向 - 当前线路为【添加创意】
           * - 素材 - 素材标签为【使用[1]】
           */
        const channelJudgment = () => {
          const { campaign_config } = this.formFields;
          const { base_form: { config: { is_label }}} = campaign_config.config;

          const is_use = is_label.value === 1;

          const judgment = [
            (module_key === TARGETING_MODULE_KEY && this.isAddCreative),
            (module_key === MATERIAL_MODULE_KEY && is_use),
          ];

          return !(judgment.some(j => j));
        };

        return channelJudgment();
      };

      return (strategy[channel_interface] || strategy['default'])() && defaultJudgment();
    },
    /**
     * 生成基础表单预览配置
     * @param {Object} form_fields 模块表单配置
     * @returns {Object}
     */
    generateBasicFormPreview(form_fields) {
      const previewed_config = clone(form_fields);
      const form_context = this.local_sharing_data.getFormContext();
      modulesCarousel(previewed_config, ({ form_config, module_key, fields_key }) => {
        const raw_config = clone(form_config.config);
        transformFormToPreview({ form_config: form_config.config });
        applyFormFieldsOverrides({
          form_config: form_config.config,
          overrides: form_context.getOverrides(),
          raw_config,
          module_key,
          fields_key,
        });
      });
      return previewed_config;
    },
    /**
     * 基础表单部署
     * @param {string} field_key 模块键名
     */
    onBasicDeploy(field_key) {
      if (this.isAddCreative && field_key === CAMPAIGN_MODULE_KEY) {
        this.$emit('addCreative');
        return;
      }
      const module_config = clone(this.formFields);
      const form_context = this.local_sharing_data.getFormContext();
      const { dataController } = form_context.config;
      form_context.addEvent(module_config, dataController);
      const form_fields = module_config[field_key];
      const steps = Object.entries(form_fields.config).map(
        ([key, { title }]) => ({ title, key: [field_key, key, 'assign', 'wrap'].join('__') })
      );
      Object.assign(this.step_form_config, {
        field_key,
        form_fields,
        module_config,
        steps,
        props: {
          ...this.step_form_config.props,
          title: `${form_fields.title}配置`,
        },
        is_show: true,
      });
    },
    /**
     * 分配表单部署
     * @param {string} field_key 模块键名
     */
    onAssignDeploy(field_key) {
      const module_config = clone(this.formFields);
      const form_context = this.local_sharing_data.getFormContext();
      const { dataController } = form_context.config;
      form_context.addEvent(module_config, dataController);
      const form_fields = module_config[field_key];
      const strategy = {
        [TITLE_MODULE_KEY]: () => {
          form_fields.type = 'library';
        },
        default: () => {},
      };
      (strategy[field_key] || strategy.default)();
      Object.assign(this.assign_form_config, {
        field_key,
        form_fields,
        props: {
          ...this.assign_form_config.props,
          title: `${form_fields.title}配置`,
        },
        is_show: true,
      });
    },
    /**
     * 弹窗元素定位
     * @param {*} selectors
     */
    onAnchor(key) {
      this.$refs.step_form_ref.onScroll(key);
    },
    /**
     * 取消基础表单配置
     */
    onBasicCannelDeploy() {
      Object.assign(this.step_form_config, { field_key: '', is_show: false });
    },
    /**
     * 取消分配表单配置
     */
    onAssignCannelDeploy() {
      Object.assign(this.assign_form_config, { field_key: '', is_show: false });
    },
    /**
     * 提交基础配置
     */
    async onBasicSubmitDeploy() {
      const ref = this.$refs['step_form_ref'];
      const valid = await ref.validateAllForm(true, true);
      if (valid && ref.parentKey) {
        // 表单效验
        if (ref.parentKey === CAMPAIGN_MODULE_KEY) {
          const { config: { init_ops: { channel_interface }}} = this.local_sharing_data.getFormContext();
          /**
           * 效验转化事件填充的完整性
           * @returns {boolean}
           */
          const validConvertEvent = () => {
            const {
              delivery_form: {
                config: {
                  fusion__channel_code: {
                    children: {
                      fusion__channel_codes,
                    },
                  },
                },
              },
              budget_and_bid_form: {
                config: {
                  convert_id,
                },
              },
            } = ref.formFields.config;
            const is_valid = fusion__channel_codes.value.every(({ list }) => {
              return list.every(({ conversion }) => !!conversion);
            });
            const valid_result = !convert_id.hidden ? is_valid : true;
            !valid_result && this.$message.warning('请完整选择转化目标');
            return valid_result;
          };
          const strategy = {
            [CHANNEL_INTERFACE_TYPE.GDT]: validConvertEvent,
            [CHANNEL_INTERFACE_TYPE.BAIDU]: validConvertEvent,
          };
          if (strategy[channel_interface]) {
            const valid_result = strategy[channel_interface]();
            if (!valid_result) {
              return;
            }
          }
        }
        const old_form_fields = clone(this.formFields[ref.parentKey]);
        this.formFields[ref.parentKey] = ref.formFields;
        const raw_ref_form_model = clone(ref.form_model);
        const raw_form_model = clone(this.form_model);
        Object.assign(this.form_model, ref.form_model);
        this.form_assign_data = convertPathObject(this.form_model);
        modulesCarousel(this.formFields, ({ form_config }) => executeFormFieldsEventListener(form_config.config));
        this.onBasicCannelDeploy();
        const form_context = this.local_sharing_data.getFormContext();
        if (ref.parentKey === CAMPAIGN_MODULE_KEY) {
          if (this.isTemplate && !form_context.strategy.isReRenderCreative(old_form_fields, this.formFields[ref.parentKey])) {
            return;
          }
          const { dataController } = form_context.config;
          this.set_dynamic_component_params = {
            config: this.formFields,
            controller: dataController,
            payload: {
              raw_ref_form_model,
              raw_form_model,
              form_assign_data: this.form_assign_data,
              extra_base_params: {
                filtering: {
                  site_sets: raw_ref_form_model['campaign_config.site_form']?.site_sets,
                },
              },
            },
          };
          await this.setDynamicComponent();
        }
        /**
         * setSourceFormFieldsData和setSourceFormFieldsDataOfCampaign
         * 这里后续应合并为同一个方法，几乎影响全渠道，影响较大延后优化
         */
        if (ref.parentKey === CREATIVE_MODULE_KEY) {
          form_context.strategy.setSourceFormFieldsData(this.formFields, this.step_form_config.module_config, this.form_assign_data);
        }
        if (ref.parentKey === CAMPAIGN_MODULE_KEY) {
          form_context.strategy.setSourceFormFieldsDataOfCampaign(this.formFields, this.step_form_config.module_config, this.form_assign_data);
          this.pullChannelPackage();
        }
      }
    },
    /**
     * 设置动态组件
     * @returns {Promise}
     */
    async setDynamicComponent() {
      const loading = this.$loading({ target: this.$refs.basic_deploy_creative_config[0].$el, lock: false, text: '生 成 创 意 中...', spinner: 'el-icon-loading', customClass: 'fusion-main-view-loading-mask' });
      try {
        const form_context = this.local_sharing_data.getFormContext();
        const { config, controller, payload } = this.set_dynamic_component_params;
        await form_context.strategy.setDynamicComponent(config, controller, payload);
        this.is_set_dynamic_component_fail = false;
      } catch (error) {
        console.error(error);
        this.is_set_dynamic_component_fail = true;
        this.$message.error('创意生成失败!');
      } finally {
        loading.close();
      }
    },
    /**
     * 提交分配配置
     */
    async onAssignSubmitDeploy() {
      const { getAssignSelection } = this.$refs.assign_edit_form_ref;
      const assign_selection = await getAssignSelection();
      const { field_key } = this.assign_form_config;
      const { config: { init_ops: { advertiser_ids }}} = this.local_sharing_data.getFormContext();
      const setting_strategy = {
        [TITLE_MODULE_KEY]: () => {
          const component_map = {
            'title-component': 'component',
            'title-edit-form': 'library',
          };
          const component_name = this.assign_form_config.components_map[field_key]();
          this.assign_form_fields[field_key].type = component_map[component_name];
        },
        default: () => {},
      };
      (setting_strategy[field_key] || setting_strategy.default)();
      const validate_strategy = {
        [TARGETING_MODULE_KEY]: () => {
          return advertiser_ids.every(adv_id => assign_selection.some(({ advertiser_id }) => adv_id === advertiser_id));
        },
        [TITLE_MODULE_KEY]: () => {
          const is_component = this.assign_form_fields[field_key].type === 'component';
          const is_valid = advertiser_ids.every(adv_id => assign_selection.some(({ advertiser_id }) => adv_id === advertiser_id));
          return is_component ? is_valid : true;
        },
        default: () => {
          return true;
        },
      };
      if (!(validate_strategy[field_key] || validate_strategy.default)()) {
        const map_name = {
          [TARGETING_MODULE_KEY]: '定向',
          [TITLE_MODULE_KEY]: '标题',
        };
        this.$message.warning(`请按广告主完整分配${map_name[field_key]}`);
        return;
      }
      const strategy = {
        [MATERIAL_MODULE_KEY]: () => {
          this.assign_form_fields[field_key].config.material_form.config.material.value = assign_selection;
        },
        [TITLE_MODULE_KEY]: () => {
          /** 如果是标题组件，则需要对文案内容进行排重处理 */
          const { selection_config } = this.assign_form_fields[field_key];
          const is_component = this.assign_form_fields[field_key].type === 'component';
          selection_config.list = is_component ? unique(assign_selection, 'name') : assign_selection;
        },
        default: () => {
          const { selection_config } = this.assign_form_fields[field_key];
          selection_config.list = assign_selection;
        },
      };
      (strategy[field_key] || strategy.default)();
      Object.assign(this.form_model, { [field_key]: assign_selection });
      /** 如果是标题组件，则需要对文案内容进行排重处理 */
      if (field_key === TITLE_MODULE_KEY) {
        const is_component = this.assign_form_fields[field_key].type === 'component';
        Object.assign(this.form_model, {
          [field_key]: is_component ? unique(assign_selection, 'name') : assign_selection,
        });
      }
      this.form_assign_data = convertPathObject(this.form_model);
      this.onAssignCannelDeploy();
    },
    /**
     * 获取当前配置的选项枚举值
     */
    async getOptionsEnumsVal() {
      const { campaign_config } = clone(this.formFields);
      const { delivery_form, site_form, budget_and_bid_form } = campaign_config.config;
      const { delivery_content_id, delivery_platform_type, fusion__channel_code } = delivery_form.config;
      const { site_sets } = site_form.config;
      const { fusion__optimize_goal, price_type, deep_convert_type } = budget_and_bid_form.config;
      const form_context = this.local_sharing_data.getFormContext();
      const { dataController, init_ops: { channel_interface }} = form_context.config;
      const convert_goal_enums_val = await dataController.getConvertGoalEnumVal();
      const strategy = {
        [CHANNEL_INTERFACE_TYPE.BILIBILI]: () => {
          const convert_goal_options_enums_val = {};
          fusion__optimize_goal.options.forEach(({ label, value }) => {
            Object.assign(convert_goal_options_enums_val, { [value]: label });
          });
          return {
            convert_goal: convert_goal_options_enums_val,
          };
        },
        [CHANNEL_INTERFACE_TYPE.OPPO]: () => {
          const convert_goal_options_enums_val = {};
          fusion__optimize_goal.options.forEach(({ label, value }) => {
            Object.assign(convert_goal_options_enums_val, { [value]: label });
          });
          return {
            convert_goal: convert_goal_options_enums_val,
          };
        },
        [CHANNEL_INTERFACE_TYPE.VIVO]: () => {
          const convert_goal_options_enums_val = {};
          fusion__optimize_goal.options.forEach(({ label, value }) => {
            Object.assign(convert_goal_options_enums_val, { [value]: label });
          });
          return {
            convert_goal: convert_goal_options_enums_val,
          };
        },
        [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
          const { deep_bid_type } = budget_and_bid_form.config;
          const convert_goal_options_enums_val = {};
          fusion__optimize_goal.options.forEach(({ label, value }) => {
            Object.assign(convert_goal_options_enums_val, { [value]: label });
          });
          const deep_bid_type_options_enums_val = {};
          deep_bid_type.options.forEach(({ label, value }) => {
            Object.assign(deep_bid_type_options_enums_val, { [value]: label });
          });
          return {
            convert_goal: convert_goal_options_enums_val,
            deep_bid_type: deep_bid_type_options_enums_val,
          };
        },
        [CHANNEL_INTERFACE_TYPE.BAIDU]: () => {
          const convert_goal_options_enums_val = {};
          fusion__optimize_goal.options.forEach(({ label, value }) => {
            Object.assign(convert_goal_options_enums_val, { [value]: label });
          });
          return {
            convert_goal: convert_goal_options_enums_val,
          };
        },
      };
      const advertiser_ids = {
        options: fusion__channel_code.children.fusion__channel_codes.value.map(({ advertiser_id, label }) => ({ label, value: advertiser_id })),
      };
      const options_config = {
        delivery_content_id,
        delivery_platform_type,
        site_sets,
        price_type,
        deep_convert_type,
        advertiser_ids,
      };
      const options_enums_val = Object.keys(options_config).reduce((pre, acc) => {
        return Object.assign(pre, {
          [acc]: options_config[acc].options.reduce((accumulator, { label, value }) => {
            return Object.assign(accumulator, {
              [value]: label,
            });
          }, {}),
        });
      }, { convert_goal: convert_goal_enums_val });
      Object.assign(this.options_enums_val, { ...options_enums_val, ...(strategy[channel_interface] && { ...strategy[channel_interface]() }) });
    },
    /**
     * 获取当前配置的选项值
     */
    getOptionsVal() {
      const form_context = this.local_sharing_data.getFormContext();
      const { init_ops: { channel_interface }} = form_context.config;
      const { campaign_config } = clone(this.formFields);
      const { budget_and_bid_form } = campaign_config.config;
      const { fusion__optimize_goal, deep_convert_type } = budget_and_bid_form.config;

      const strategy = {
        [CHANNEL_INTERFACE_TYPE.VIVO]: () => {
          return {
            convert_goal: clone(fusion__optimize_goal.options),
            deep_convert_type: clone(deep_convert_type.options),
          };
        },
        default: () => {
          return {};
        },
      };

      Object.assign(this.options_val, { ...(strategy[channel_interface] || strategy['default'])() });
    },
    /**
     * 验证创意配置
     * @param {Object} data 分配后的表单数据
     * @returns {Promise<boolean>}
     */
    async validateCreativeConfig(data) {
      const { creative_config, material_config, title_config = [], site_config = [] } = data;
      const { delivery_mode } = creative_config.content_form;
      const { config: { init_ops: { channel_interface }}} = this.local_sharing_data.getFormContext();
      /** 组件化创意值映射 */
      const map_field_value = {
        [CHANNEL_INTERFACE_TYPE.GDT]: 'DELIVERY_MODE_COMPONENT',
        [CHANNEL_INTERFACE_TYPE.BILIBILI]: '1',
        [CHANNEL_INTERFACE_TYPE.OPPO]: OPTIONS_TYPE.UNLIMITED,
        [CHANNEL_INTERFACE_TYPE.VIVO]: '1',
        [CHANNEL_INTERFACE_TYPE.JRTT]: 'DELIVERY_MODE_COMPONENT',
        [CHANNEL_INTERFACE_TYPE.BAIDU]: '1',
      };
      const strategy = {
        /** 华为渠道没有创意投放模式 */
        [CHANNEL_INTERFACE_TYPE.HUAWEI]: () => {
          return true;
        },
        [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
          /**
           * 模块满足以下条件时，不支持分配配置
           * - 素材 - 关联星广联投为【启用(ON)】 或 素材类型为【直播素材['LIVE_MATERIALS']】
           * - 标题 - 使用原视频标题为【启用(ON)】 或 素材类型为【直播素材['LIVE_MATERIALS']】
           * - 落地页
           *   - 素材类型可见 且 投放平台为【Android应用['ANDROID']】或投放平台为【IOS应用['IOS']】
           *     - 素材类型为【直播素材['LIVE_MATERIALS']】
           *     - 素材类型为【广告素材['PROMOTION_MATERIALS']】 且 营销场景为【直播['LIVE']】
           */
          const { campaign_config, creative_config } = this.formFields;
          const { delivery_form: { config: { delivery_platform_type, marketing_goal, star_task_switch }}} = campaign_config.config;
          const { content_form: { config: { materials_type, original_video_title }}} = creative_config.config;

          const is_app = ['ANDROID', 'IOS'].includes(delivery_platform_type.value);
          const is_live = marketing_goal.value === 'LIVE';
          const is_star_tasks_on = star_task_switch.value === 'ON';
          const is_materials_type_visible = !materials_type.hidden;
          const is_promotion_materials = materials_type.value === 'PROMOTION_MATERIALS';
          const is_live_materials = materials_type.value === 'LIVE_MATERIALS';
          const is_original_video_title_show = !original_video_title.hidden;
          const is_original_video_title_on = original_video_title.value === 'ON';

          // 当前仅做特殊处理，遇到情况，则不做验证；后续应做更精准的逻辑判断
          if (is_star_tasks_on || (is_original_video_title_show && is_original_video_title_on) || (is_materials_type_visible && is_live_materials) || (is_materials_type_visible && is_promotion_materials && is_live && is_app)) {
            return true;
          } else {
            return delivery_mode !== map_field_value[channel_interface] || !delivery_mode;
          }
        },
        default: () => {
          return delivery_mode !== map_field_value[channel_interface] || !delivery_mode;
        },
      };
      const defaultJudgment = () => {
        /**
         * 模块满足以下条件时，不支持分配配置
         * - 素材 - 素材标签为【使用[1]】
         */
        const { campaign_config } = this.formFields;
        const { base_form: { config: { is_label }}} = campaign_config.config;

        const is_use = is_label.value === 1;

        // TODO - 后续应作更精准的判断
        return is_use;
      };
      const valid = (strategy[channel_interface] || strategy.default)() || defaultJudgment();
      /** 如果创意投放模式没有值或创意投放模式非组件化创意，则不做验证 */
      if (valid) {
        return Promise.resolve(true);
      }
      const { images = [], videos = [] } = material_config;
      const { videos_num_per_creative, images_num_per_creative, titles_num_per_creative, sites_num_per_creative } = creative_config.content_form;
      const getMessage = (type, number) => {
        const name_map = {
          images: '图片',
          videos: '视频',
          titles: '标题',
          sites: '落地页',
        };
        return `请至少添加${number}个${name_map[type]}`;
      };
      const validations = [
        {
          filed: 'images_length',
          rules: [
            {
              message: getMessage('images', images_num_per_creative),
              validator: (value) => {
                return value >= images_num_per_creative;
              },
            },
          ],
          value: images.length,
        },
        {
          filed: 'videos_length',
          rules: [
            {
              message: getMessage('videos', videos_num_per_creative),
              validator: (value) => {
                return value >= videos_num_per_creative;
              },
            },
          ],
          value: videos.length,
        },
        {
          filed: 'titles_length',
          rules: [
            {
              message: getMessage('titles', titles_num_per_creative),
              validator: (value) => {
                return value >= titles_num_per_creative;
              },
            },
          ],
          value: title_config.length,
        },
        {
          filed: 'sites_length',
          rules: [
            {
              message: getMessage('sites', sites_num_per_creative),
              validator: (value) => {
                return value === 0 || value >= sites_num_per_creative;
              },
            },
          ],
          value: site_config.length,
        },
      ];
      return await rulesValidator(validations);
    },
    /**
     * 预览
     */
    async onPreview() {
      const loading = this.$loading({ lock: true, text: '提 交 预 览 中...', spinner: 'el-icon-loading', customClass: 'fusion-main-view-loading-mask' });
      try {
        this.raw_form_assign_data = clone(convertPathObject(this.form_model));
        await this.validateCreativeConfig(this.raw_form_assign_data);
        this.assign_title_component_type = this.formFields.title_config.type;
        const { config: { init_ops }, strategy } = this.local_sharing_data.getFormContext();
        const params = strategy.transformPreviewPayload({
          form_assign_data: this.raw_form_assign_data,
          init_ops,
          extra_config: {
            is_add_creative: this.isAddCreative,
            add_creative_list: this.addCreativeList,
          },
        });
        const { list } = await getPreviewPlanApi(params);
        this.advertiser_sources = generateAdvertiserSourcesData(list);
        this.getOptionsVal();
        await this.getOptionsEnumsVal();
        this.dynamic_table_sources = generateDynamicTableData(this.advertiser_sources);
        this.setTemplateData();
      } catch (error) {
        console.error(error);
        this.$message.error(error.message || '生成预览失败!');
      } finally {
        loading.close();
        this.$refs?.preview_table_ref?.closeAllTablePopover?.();
      }
    },

    /**
     * 复制创意
     * @param {Object} config
     * @param {string} config.target 要复制的目标键名
     * @param {string} config.payload 数据的位置信息
     */
    onCopy({ target, payload }) {
      const { advertiser_id, campaign: campaign_index, creative: creative_index } = payload.coordinate;
      const campaigns = clone(this.advertiser_sources[advertiser_id].campaigns);
      const form_context = this.local_sharing_data.getFormContext();
      const { channel_config } = form_context.config;
      const {
        creative: {
          max_creative_num,
        },
      } = channel_config;
      const strategy = {
        creative: () => {
          if (campaigns[campaign_index].creatives.length < max_creative_num) {
            const creative = clone(campaigns[campaign_index].creatives[creative_index]);
            campaigns[campaign_index].creatives.unshift(creative);
          } else {
            this.$message.warning('创意数量已达上限！');
          }
        },
      };
      strategy[target]();
      this.$set(this.advertiser_sources, advertiser_id, { campaigns });
      this.dynamic_table_sources = generateDynamicTableData(this.advertiser_sources);
    },

    /**
     * 移除广告/创意
     * @param {Object} config
     * @param {string} config.target 要复制的目标键名
     * @param {string} config.payload 数据的位置信息
     */
    onRemove({ target, payload }) {
      const { campaign: campaign_index, creative: creative_index, advertiser_id } = payload.coordinate;
      const campaigns = clone(this.advertiser_sources[advertiser_id].campaigns);
      const strategy = {
        campaign: () => {
          campaigns.splice(campaign_index, 1);
        },
        creative: () => {
          if (campaigns[campaign_index].creatives.length > 1){
            campaigns[campaign_index].creatives.splice(creative_index, 1);
          } else {
            this.$message.warning('至少包含一项');
          }
        },
      };
      strategy[target]();
      this.advertiser_sources[advertiser_id]['campaigns'] = campaigns;
      this.dynamic_table_sources = generateDynamicTableData(this.advertiser_sources);
    },

    /**
     * 获取账户下的计划列表
     * @param {number} advertiser_id 广告主id
     * @returns {Object[]}
     */
    getAdvertiserCampaigns(advertiser_id) {
      return this.advertiser_sources[advertiser_id].campaigns || [];
    },

    /**
     * 提交数据创建广告
     */
    async onSubmit() {
      try {
        this.submit_loading = true;
        const {
          config: { init_ops: { channel_interface }},
          strategy,
        } = this.local_sharing_data.getFormContext();
        const payload = strategy.transformSubmitPayload({ advertiser_sources: this.advertiser_sources, channel_interface });
        const result = await createPlanApi(payload);
        this.is_save_template && await templateResource.addTemplateData({
          advertiser: channel_interface,
          type: `${channel_interface}_fusion_plans`,
          ...this.template_data,
          name: `${this.template_data.name}|${randomUUID()}`,
        });
        this.$confirm(result?.message || '提交成功', {
          showClose: false,
          closeOnClickModal: false,
          closeOnPressEscape: false,
          confirmButtonText: '保留当前页配置',
          cancelButtonText: '重置当前页配置',
        }).catch(() => {
          this.$emit('reset');
        });
      } finally {
        this.submit_loading = false;
      }
    },
    /**
     * 存储模板数据
     * @returns {void}
     */
    setTemplateData() {
      const { strategy } = this.local_sharing_data.getFormContext();
      const form_fields = clone(this.formFields);
      const {
        campaign_config,
        creative_config,
        material_config,
        title_config,
      } = form_fields;
      const {
        base_form: {
          config: {
            campaign_name,
          },
        },
      } = campaign_config.config;
      const template_map = strategy.getTemplateMap();
      const template_target = { campaign_config, creative_config };

      /**
       * 模块数据对应模板表分层级如下：
       * 第一层级 - 如 campaign_config、creative_config 等
       * 第二层级 - 如 base_form、delivery_form 等
       * 第三层级 - 如 campaign_name、delivery_platform_type 等
       */
      for (const [template_key, template_config] of Object.entries(template_map)) {
        // 第一层级配置
        const form_fields = template_target[template_key].config;

        for (const [field_key, field_item] of Object.entries(form_fields)) {
          // 移除第一层级的属性
          if (!(field_key in template_config)) {
            delete form_fields[field_key];
            continue;
          }

          // 第二层级的模板表为空数组时，将保存整个层级
          const allowed_keys = template_config[field_key];
          if (!allowed_keys?.length) {
            continue;
          }

          // 移除第二层级的属性
          const item_config = field_item.config;
          for (const config_key of Object.keys(item_config)) {
            if (!allowed_keys.includes(config_key)) {
              delete item_config[config_key];
            }
          }
        }
      }

      const payload = { ...template_target, material_config, title_config };

      // 处理一些特殊结构的模板数据
      strategy.handleTemplateStoragePayload?.(payload);

      this.template_data = {
        name: campaign_name.value,
        template_data: JSON.stringify(payload),
      };
    },
    /**
     * 使用媒体文案组件
     * @param {string} field_key 模块键名
     * @returns {void}
     */
    onUseMediaComponent(field_key) {
      const module_config = clone(this.formFields);
      const form_context = this.local_sharing_data.getFormContext();
      const { dataController } = form_context.config;
      form_context.addEvent(module_config, dataController);
      const form_fields = module_config[field_key];
      form_fields.type = 'component';
      Object.assign(this.assign_form_config, {
        field_key,
        form_fields,
        props: {
          ...this.assign_form_config.props,
          title: `${form_fields.title}组件配置`,
        },
        is_show: true,
      });
    },
    /**
     * 重新排序
     * @param {string} field_key 模块键名
     * @returns {void}
     */
    shuffleModule(field_key) {
      const form_fields = this.formFields[field_key];
      const {
        config: {
          material_form: {
            config: {
              material: {
                value: material_value,
              },
            },
          },
        },
      } = form_fields;
      const current_tab = `${material_value.tab}s`;
      const target = material_value[current_tab];
      const result = shuffleArray(target);
      material_value[current_tab].splice(0, target.length, ...result);
    },

    /**
     * 同步渠道包
     * @returns {void}
     */
    pullChannelPackage() {
      const { config: { init_ops: { channel_interface, advertiser_ids }}} = this.local_sharing_data.getFormContext();

      const strategy = {
        [CHANNEL_INTERFACE_TYPE.GDT]: () => {
          const { campaign_config: { config: { delivery_form }}} = this.formFields;
          const { delivery_platform_type, delivery_content_id } = delivery_form.config;
          const is_android = delivery_platform_type.value === 'PROMOTED_OBJECT_TYPE_APP_ANDROID';

          /**
           * 仅支持安卓
           */
          if (!is_android) {
            return;
          }

          const payload = {
            filtering: {
              delivery_content_id: delivery_content_id.value.split('#')[0],
            },
          };

          advertiser_ids.forEach(advertiser_id => {
            pullChannelPackageApi({
              channel_interface,
              advertiser_id,
              ...payload,
            });
          });
        },
        [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
          const { campaign_config: { config: { delivery_form }}} = this.formFields;
          const { delivery_platform_type, delivery_content_id } = delivery_form.config;
          const is_android = delivery_platform_type.value === 'ANDROID';

          /**
           * 仅支持安卓
           */
          if (!is_android) {
            return;
          }

          advertiser_ids.forEach(advertiser_id => {
            const delivery_content_map = delivery_content_id.value.result_map;
            const delivery_content_value = delivery_content_map[advertiser_id].value.split('#')[0];
            const payload = {
              filtering: {
                delivery_content_id: delivery_content_value,
              },
            };
            pullChannelPackageApi({
              channel_interface,
              advertiser_id,
              ...payload,
            });
          });
        },
        [CHANNEL_INTERFACE_TYPE.BAIDU]: () => {
          const { campaign_config: { config: { delivery_form }}} = this.formFields;
          const { delivery_platform_type } = delivery_form.config;
          const is_android = delivery_platform_type.value === '3';

          /**
           * 仅支持安卓
           */
          if (!is_android) {
            return;
          }

          advertiser_ids.forEach(advertiser_id => {
            pullChannelPackageApi({ channel_interface, advertiser_id });
          });
        },
      };

      strategy[channel_interface]?.();
    },
  },
};
</script>

<style lang="scss" scoped>
.main-view-wrapper {
  .setting-btn {
    color: #409eff;
    font-size: 14px;
    &:hover {
      color: #66b1ff;
    }
  }
}
</style>

<style lang="scss">
.fusion-main-view-loading-mask .el-icon-loading {
  font-size: 32px;
  margin-bottom: 15px;
}
</style>
